import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Building, MapPin, Phone, Mail, Globe, MessageCircle, ChevronRight, BadgeCheck,
} from 'lucide-react';
import { isUuid } from '@/lib/idOrSlug';

export default function DeveloperDetail() {
  const { slug } = useParams<{ slug: string }>();
  const col = isUuid(slug) ? 'id' : 'slug';

  const { data: developer, isLoading } = useQuery({
    queryKey: ['developer', slug],
    queryFn: async () => {
      const { data } = await supabase
        .from('developer_profiles')
        .select('*')
        .eq(col, slug!)
        .maybeSingle();
      return data;
    },
    enabled: !!slug,
  });

  const { data: projects } = useQuery({
    queryKey: ['developer-projects', developer?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('projects')
        .select('id, slug, name, city, sub_city, status, project_images(url, is_primary, sort_order)')
        .eq('developer_id', developer!.id)
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false });
      return data || [];
    },
    enabled: !!developer?.id,
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="container py-8 space-y-4">
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </Layout>
    );
  }

  if (!developer) {
    return (
      <Layout>
        <div className="container py-20 text-center">
          <Building className="h-14 w-14 mx-auto text-muted-foreground/50 mb-4" />
          <h1 className="text-2xl font-bold">Developer not found</h1>
          <Link to="/projects" className="text-primary hover:underline text-sm">Browse projects</Link>
        </div>
      </Layout>
    );
  }

  const description = typeof developer.description === 'string'
    ? developer.description
    : (developer.description as any)?.en || '';

  return (
    <Layout>
      <SEOHead
        title={`${developer.company_name} — Real Estate Developer`}
        description={`${developer.company_name} is a real estate developer${developer.city ? ' in ' + developer.city : ''}. Explore active and upcoming projects.`}
        canonical={`/developers/${developer.slug || developer.id}`}
      />

      <div className="border-b bg-card">
        <div className="container py-3">
          <nav className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/projects" className="hover:text-foreground">Projects</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground font-medium">{developer.company_name}</span>
          </nav>
        </div>
      </div>

      <div className="container py-6 space-y-8">
        {/* Header */}
        <div className="flex items-start gap-4 p-4 rounded-xl border bg-card">
          <div className="h-20 w-20 rounded-md border overflow-hidden shrink-0 bg-muted">
            {developer.logo_url ? (
              <img src={developer.logo_url} alt={developer.company_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-lg font-bold text-muted-foreground">
                {developer.company_name.slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl md:text-2xl font-bold">{developer.company_name}</h1>
              {developer.is_verified && (
                <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-300 gap-1">
                  <BadgeCheck className="h-3 w-3" /> Verified
                </Badge>
              )}
            </div>
            {developer.city && (
              <p className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" /> {developer.city}{developer.address ? `, ${developer.address}` : ''}
              </p>
            )}
            <div className="flex items-center gap-4 mt-2 text-sm flex-wrap">
              <span><strong>{projects?.length || 0}</strong> Projects</span>
              {developer.established_year && (
                <span className="text-muted-foreground">Est. <strong className="text-foreground">{developer.established_year}</strong></span>
              )}
              {developer.employee_count && (
                <span className="text-muted-foreground"><strong className="text-foreground">{developer.employee_count}+</strong> Staff</span>
              )}
            </div>
          </div>
        </div>

        {/* Contact strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {developer.phone && (
            <Button asChild variant="outline" className="gap-2 justify-start"><a href={`tel:${developer.phone}`}><Phone className="h-4 w-4" /> Call</a></Button>
          )}
          {developer.whatsapp && (
            <Button asChild variant="outline" className="gap-2 justify-start"><a href={`https://wa.me/${developer.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /> WhatsApp</a></Button>
          )}
          {developer.email && (
            <Button asChild variant="outline" className="gap-2 justify-start"><a href={`mailto:${developer.email}`}><Mail className="h-4 w-4" /> Email</a></Button>
          )}
          {developer.website && (
            <Button asChild variant="outline" className="gap-2 justify-start"><a href={developer.website} target="_blank" rel="noreferrer"><Globe className="h-4 w-4" /> Website</a></Button>
          )}
        </div>

        {/* About */}
        {description && (
          <section>
            <h2 className="text-lg font-bold mb-3">About {developer.company_name}</h2>
            <Card className="rounded-xl"><CardContent className="p-5">
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{description}</p>
            </CardContent></Card>
          </section>
        )}

        {/* Projects */}
        <section>
          <h2 className="text-lg font-bold mb-4">Projects by {developer.company_name}</h2>
          {!projects || projects.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center bg-muted/30 rounded-lg">
              No published projects yet.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((p: any) => {
                const cover = null
                  || (p.project_images || []).find((i: any) => i.is_primary)?.url
                  || (p.project_images || []).slice().sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0))[0]?.url;
                const name = (p.name?.en) || p.name || 'Project';
                return (
                  <Link key={p.id} to={`/projects/${p.slug || p.id}`} className="group rounded-xl overflow-hidden border bg-card hover:shadow-lg transition-shadow">
                    <div className="aspect-[4/3] bg-muted overflow-hidden">
                      {cover ? (
                        <img src={cover} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Building className="h-10 w-10" /></div>
                      )}
                    </div>
                    <div className="p-3 space-y-1">
                      <Badge variant="outline" className="text-[10px] capitalize">{p.status?.replace('_', ' ') || 'Project'}</Badge>
                      <h3 className="font-semibold text-sm truncate group-hover:text-primary">{name}</h3>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                        <MapPin className="h-3 w-3" /> {p.city}{p.sub_city ? `, ${p.sub_city}` : ''}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
